import React, {FC, InputHTMLAttributes, useEffect, useRef, useState} from 'react';
import {useId} from 'react-id-generator';
import {Button} from './Button';
import {Label} from './Label';
import {TextField} from './TextField';
import {userPhoneVerify, userVerifications} from '../apis/user';

export interface PhoneNumberProps
  extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  value?: string;
  onVerified?: (phone: string) => void;
  needVerify?: boolean;
}

export const PhoneNumber: FC<PhoneNumberProps> = ({
  label,
  value = '',
  onVerified,
  onChange,
  needVerify = false,
  ...props
}) => {
  const [id] = useId(1, 'phonenumber');
  const [number1, setNumber1] = useState('');
  const [number2, setNumber2] = useState('');
  const [number3, setNumber3] = useState('');

  useEffect(() => {
    const matched = value.match(/([0-9]{1,3})([0-9]{1,4})?([0-9]{1,4})?/);
    setNumber1(matched?.[1] ?? '');
    setNumber2(matched?.[2] ?? '');
    setNumber3(matched?.[3] ?? '');
  }, [value]);

  const [isCodeSent, setIsCodeSent] = useState(false);
  const [vCode, setVCode] = useState<string>('');
  const [verified, setVerified] = useState<boolean>(false);

  const phoneFirstRef = useRef<any>(null);
  const phoneMiddleRef = useRef<any>(null);
  const phoneLastRef = useRef<any>(null);

  const sendCode = () => {
    if (number1 && number2 && number3) {
      const phone = `${number1}${number2}${number3}`;
      userVerifications(phone).then(r => setIsCodeSent(true));
    }
  };

  const verify = () => {
    const phone = `${number1}${number2}${number3}`;
    vCode &&
      userPhoneVerify({code: vCode, phone}).then(r => {
        setVerified(true);
        onVerified && onVerified(phone);
      });
  };

  return (
    <div className="label-col gap-2">
      {label && <Label htmlFor={id} text={label} />}
      <div className="grid grid-cols-3 gap-4">
        <TextField
          id={id}
          type="tel"
          pattern="[0-9]{3}"
          maxLength={3}
          disabled={verified}
          placeholder="000"
          value={number1}
          onChange={e => {
            if(e.target.value.length == 3){phoneMiddleRef.current.focus()}
            setNumber1(e.target.value);
            e.target.value = `${e.target.value}${number2}${number3}`;
            onChange?.(e);
          }}
          ref={phoneFirstRef}
          {...props}
        />
        <TextField
          type="tel"
          pattern="[0-9]{4}"
          maxLength={4}
          disabled={verified}
          placeholder="0000"
          value={number2}
          onChange={e => {
            if(e.target.value.length == 0){phoneFirstRef.current.focus()}
            else if(e.target.value.length == 4){phoneLastRef.current.focus()}
            setNumber2(e.target.value);
            e.target.value = `${number1}${e.target.value}${number3}`;
            onChange?.(e);
          }}
          ref={phoneMiddleRef}
          {...props}
        />
        <TextField
          type="tel"
          pattern="[0-9]{4}"
          maxLength={4}
          disabled={verified}
          placeholder="0000"
          value={number3}
          onChange={e => {
            if(e.target.value.length == 0){phoneMiddleRef.current.focus()}
            setNumber3(e.target.value);
            e.target.value = `${number1}${number2}${e.target.value}`;
            onChange?.(e);
          }}
          ref={phoneLastRef}
          {...props}
        />
      </div>

      {verified && (
        <Button
          type="button"
          disabled={true}
          text={`인증 완료`}
          className={`${
            isCodeSent ? 'outlined-brand-1' : 'text-brand-1 filled-brand-2'
          }`}
        />
      )}

      {needVerify && !verified && (
        <Button
          type="button"
          onClick={sendCode}
          text={`${isCodeSent ? '인증번호  재전송' : '인증번호 발송'} `}
          className={`${
            isCodeSent
              ? 'outlined-brand-1'
              : 'text-brand-1 bg-white border border-brand-1'
          }`}
        />
      )}

      {!verified && isCodeSent && (
        <div className="flex justify-between space-x-2">
          <div className="flex-1">
            <TextField
              type="number"
              placeholder="인증번호를 입력해주세요."
              onChange={e => {
                setVCode(e.target.value);
              }}
            />
          </div>
          <Button
            type="button"
            text="확인"
            className="filled-brand-1 px-8"
            onClick={verify}
          />
        </div>
      )}
    </div>
  );
};
